import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Clock, Send, RotateCcw } from "lucide-react";
import { trackCustomEvent } from "../utils/metaPixel";

const API = "https://bhavanaastro.onrender.com/api";

// During the FREE minute, stop the clock while the AI is thinking / writing its reply,
// so the visitor really gets a minute of conversation. Set to false to keep a plain countdown.
const PAUSE_TRIAL_WHILE_AI_REPLIES = true;

const EXTEND_MINUTES = 5;
const TYPE_STEP_MS = 20; // reveal speed of AI replies: 4 characters every 20 ms
const TYPE_CHARS = 4;

const chatCss = `
  .as-chat { font-family: 'Hind', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif; }
  .as-display { font-family: 'Baloo 2', 'Hind', system-ui, sans-serif; }
  .as-sky {
    background:
      radial-gradient(1.5px 1.5px at 12% 30%, rgba(255,255,255,.7), transparent 60%),
      radial-gradient(1px 1px at 58% 18%, rgba(255,255,255,.6), transparent 60%),
      radial-gradient(1.5px 1.5px at 90% 60%, rgba(255,255,255,.5), transparent 60%),
      radial-gradient(120% 120% at 100% 0%, rgba(255,182,39,.18), transparent 60%),
      #17143F;
  }
  @keyframes as-pop {
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: none; }
  }
  @keyframes as-dot {
    0%, 80%, 100% { transform: translateY(0); opacity: .45; }
    40% { transform: translateY(-3px); opacity: 1; }
  }
  .as-msg { animation: as-pop .25s ease-out both; }
  .as-dot { animation: as-dot 1s infinite; }
  .as-dot:nth-child(2) { animation-delay: .15s; }
  .as-dot:nth-child(3) { animation-delay: .3s; }
  @media (prefers-reduced-motion: reduce) {
    .as-msg, .as-dot { animation: none; }
  }
`;

// ---------- small helpers ----------
let idCounter = 0;
const uid = () => `${Date.now()}-${idCounter++}`;

// Turns *word* and **word** from the AI into bold text.
// Safe while the reply is typed letter by letter (text.slice(0, i)).
function renderRich(text) {
  let s = String(text || "");
  // a single trailing "*" that is not part of "**"
  if (/(^|[^*])\*$/.test(s)) s = s.slice(0, -1);
  // unclosed "**": drop a dangling trailing one, otherwise close it
  const pairs = (s.match(/\*\*/g) || []).length;
  if (pairs % 2 === 1) s = s.endsWith("**") ? s.slice(0, -2) : s + "**";

  return s.split(/(\*\*[^*]+\*\*|\*[^*\n]+\*)/g).map((part, i) => {
    if (/^\*\*[^*]+\*\*$/.test(part)) return <strong key={i}>{part.slice(2, -2)}</strong>;
    if (/^\*[^*\n]+\*$/.test(part)) return <strong key={i}>{part.slice(1, -1)}</strong>;
    return part;
  });
}

const QUESTION_BANK = {
  love: ["Mera life partner kaisa hoga?", "Mere relationship ka future kya hai?"],
  marriage: ["Meri shaadi kab hogi?", "Shaadi ke liye achha samay kaun sa hai?"],
  career: ["Mera career kaisa rahega?", "Job mein badlav kab aayega?"],
  job: ["Naya job kab milega?"],
  business: ["Business mein safalta milegi?"],
  money: ["Paisa kab aayega?"],
  finance: ["Paisa kab aayega?"],
  health: ["Meri sehat kaisi rahegi?"],
  education: ["Padhai mein kaisa rahega?"],
};
const DEFAULT_QUESTIONS = [
  "Meri shaadi kab hogi?",
  "Mera career kaisa rahega?",
  "Aaj ka din mere liye kaisa hai?",
];

function questionsFor(categories) {
  const joined = (categories || []).join(" ").toLowerCase();
  const picked = [];
  Object.keys(QUESTION_BANK).forEach((key) => {
    if (joined.includes(key)) {
      QUESTION_BANK[key].forEach((q) => {
        if (!picked.includes(q)) picked.push(q);
      });
    }
  });
  return (picked.length ? picked : DEFAULT_QUESTIONS).slice(0, 4);
}

const FOLLOW_UPS = [
  {
    keys: ["shaadi", "vivah", "marriage", "partner", "love", "relationship", "rishta"],
    items: [
      "Shaadi ka sahi samay kaun sa hai?",
      "Mera life partner kaisa hoga?",
      "Rishte mein rukawat ho to kya upay hain?",
    ],
  },
  {
    keys: ["career", "job", "naukri", "business", "promotion"],
    items: [
      "Career mein agla badlav kab aa sakta hai?",
      "Mere liye job ya business, kya behtar hai?",
      "Career ke liye kaun se upay madad karenge?",
    ],
  },
  {
    keys: ["paisa", "money", "finance", "dhan", "income", "paise"],
    items: [
      "Paise ki sthiti kab behtar ho sakti hai?",
      "Mere liye bachat aur nivesh ka samay kaisa hai?",
      "Dhan ke liye kaun se upay hain?",
    ],
  },
  {
    keys: ["health", "sehat", "bimari"],
    items: [
      "Sehat ke liye kaun se samay dhyaan dene wale hain?",
      "Sehat ke liye kaun se upay madad karenge?",
    ],
  },
];
const GENERIC_FOLLOW_UPS = [
  "Meri kundli ki sabse badi taakat kya hai?",
  "Agle 6 mahine mere liye kaise rahenge?",
  "Meri kundli ke hisaab se kaun se upay madad karenge?",
];

// Next questions that fit what the user just asked (or the astrologer's own topics)
function followUpsFor(question, categories) {
  const q = (question || "").toLowerCase();
  const cats = (categories || []).join(" ").toLowerCase();
  const hit =
    FOLLOW_UPS.find((g) => g.keys.some((k) => q.includes(k))) ||
    FOLLOW_UPS.find((g) => g.keys.some((k) => cats.includes(k)));
  const list = (hit ? hit.items : GENERIC_FOLLOW_UPS).filter((x) => x.toLowerCase() !== q.trim());
  return list.slice(0, 3);
}

// Keeps the chat the size of what is really visible (phone keyboards!)
function useViewportBox() {
  const read = () => {
    const vv = window.visualViewport;
    return {
      height: vv ? vv.height : window.innerHeight,
      top: vv ? vv.offsetTop : 0,
      mobile: window.innerWidth < 768,
    };
  };
  const [box, setBox] = useState(read);
  useEffect(() => {
    const on = () => setBox(read());
    const vv = window.visualViewport;
    window.addEventListener("resize", on);
    if (vv) {
      vv.addEventListener("resize", on);
      vv.addEventListener("scroll", on);
    }
    on();
    return () => {
      window.removeEventListener("resize", on);
      if (vv) {
        vv.removeEventListener("resize", on);
        vv.removeEventListener("scroll", on);
      }
    };
  }, []);
  return box;
}

function Sheet({ children, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center bg-black/50 sm:items-center"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-t-3xl bg-white p-5 sm:rounded-3xl"
        style={{ paddingBottom: "max(1.25rem, env(safe-area-inset-bottom))" }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

const goldBtn =
  "as-display flex w-full items-center justify-center rounded-2xl bg-[#FFB627] px-6 py-3.5 text-[18px] font-bold text-[#1E1A4D] transition hover:bg-[#FFC24D] active:scale-[0.98] disabled:opacity-60";
const textBtn = "mt-2 block w-full py-2 text-center text-sm font-medium text-[#3A2E9C]";

export default function AstroChat() {
  const navigate = useNavigate();
  const location = useLocation();
  const { astrologerId } = useParams(); // which AI astrologer was picked
  const box = useViewportBox();

  const sessKey = `asChat_${astrologerId}`;
  const pendingKey = `asPendingQ_${astrologerId}`;

  const [userProfile, setUserProfile] = useState(null);
  const [astrologerInfo, setAstrologerInfo] = useState(null);
  const [astrologerPhoto, setAstrologerPhoto] = useState("/ai.png");

  const [ready, setReady] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false); // waiting for the server
  const [replying, setReplying] = useState(false); // AI reply being shown
  const [aiSuggestions, setAiSuggestions] = useState([]); // next questions made from this very chat

  const [isTrial, setIsTrial] = useState(false);
  const [total, setTotal] = useState(60);
  const [remaining, setRemaining] = useState(60);

  const [sheet, setSheet] = useState(null); // null | "confirmEnd" | "trialEnd" | "extend"
  const [sheetError, setSheetError] = useState("");
  const [payBusy, setPayBusy] = useState(false);
  const [balance, setBalance] = useState(null);

  const scrollRef = useRef(null);
  const intervalsRef = useRef(new Set());
  const endedRef = useRef(false);
  const firstSentRef = useRef(false);
  const navKeyRef = useRef(null); // which "start chat" click this saved chat belongs to
  const sendRef = useRef(null);
  const live = useRef({ isTrial: false, busy: false });
  live.current = { isTrial, busy: loading || replying };

  const secs = Math.ceil(remaining);
  const ended = ready && secs <= 0;
  const rate = astrologerInfo?.rates?.chat ?? 0;
  const cost = rate * EXTEND_MINUTES;
  const notEnough = balance !== null && cost > balance;
  const userId = userProfile?.id || userProfile?._id;

  // ---------- lock page scroll behind the chat ----------
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // stop any running "typing" timers when leaving
  useEffect(() => {
    const intervals = intervalsRef.current;
    return () => intervals.forEach((t) => clearInterval(t));
  }, []);

  // ---------- reveal an AI reply ----------
  const typeMessage = useCallback(
    (text) =>
      new Promise((resolve) => {
        const id = uid();
        setMessages((prev) => [...prev, { id, sender: "bot", text, shown: "" }]);
        setReplying(true);
        let i = 0;
        const t = setInterval(() => {
          i = Math.min(text.length, i + TYPE_CHARS);
          setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, shown: text.slice(0, i) } : m)));
          if (i >= text.length) {
            clearInterval(t);
            intervalsRef.current.delete(t);
            setReplying(false);
            resolve();
          }
        }, TYPE_STEP_MS);
        intervalsRef.current.add(t);
      }),
    []
  );

  const addSystem = (text) =>
    setMessages((prev) => [...prev, { id: uid(), sender: "system", text, shown: text }]);

  // ---------- start or restore the session ----------
  useEffect(() => {
    const stored = sessionStorage.getItem("user") || localStorage.getItem("user");
    if (!stored) {
      navigate("/login?next=/user/dashboard", { replace: true });
      return;
    }
    const profile = JSON.parse(stored);
    setUserProfile(profile);

    let saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem(sessKey) || "null");
    } catch (e) {
      saved = null;
    }

    const fromState = location.state;

    // A brand new chat was just started (new click, new navigation key): never reuse an old saved
    // chat, otherwise the old messages and a 0:00 timer show up. A refresh keeps the same key.
    const startedNew = typeof fromState?.isTrial === "boolean" && (!saved || saved.navKey !== location.key);
    if (startedNew && saved) {
      sessionStorage.removeItem(sessKey);
      saved = null;
    }
    navKeyRef.current = saved ? saved.navKey || null : location.key;

    if (!saved && typeof fromState?.isTrial !== "boolean") {
      // No chat was started for this link (for example it was pasted or bookmarked)
      navigate("/user/dashboard", { replace: true });
      return;
    }

    let cancelled = false;
    (async () => {
      let info = null;
      try {
        const res = await axios.get(`${API}/Consult-astrologers/${astrologerId}`);
        info = res.data;
      } catch (err) {
        console.error("Error fetching AI astrologer profile:", err);
      }
      if (cancelled) return;

      const cachedPhoto = sessionStorage.getItem("astrologerPhoto");
      if (info) {
        setAstrologerInfo(info);
        if (info.photo) {
          setAstrologerPhoto(info.photo);
          sessionStorage.setItem("astrologerPhoto", info.photo);
        } else if (cachedPhoto) setAstrologerPhoto(cachedPhoto);
      } else if (cachedPhoto) {
        setAstrologerPhoto(cachedPhoto);
      }

      if (saved) {
        // Refresh or coming back (for example after a wallet recharge)
        const elapsed = (Date.now() - saved.savedAt) / 1000;
        setIsTrial(!!saved.isTrial);
        setTotal(saved.total || 60);
        setRemaining(Math.max(0, saved.remaining - elapsed));
        setMessages((saved.messages || []).map((m) => ({ ...m, shown: m.text })));
        setReady(true);
        return;
      }

      // Fresh chat
      const trial = !!fromState.isTrial;
      const seconds = trial ? fromState.trialSeconds || 60 : 300;
      setIsTrial(trial);
      setTotal(seconds);
      setRemaining(seconds);

      const firstName = (profile.name || "").trim().split(" ")[0] || "dost";
      const aName = info?.name || "AI astrologer";
      const cats = info?.categories || [];
      const topics = cats.length
        ? `Aap ${cats.join(", ")} ke baare mein kuch bhi pooch sakte ho.`
        : "Aap apni kundli ke baare mein kuch bhi pooch sakte ho.";

      const dobStr = profile.dob
        ? new Date(profile.dob).toLocaleDateString("en-IN", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })
        : "";
      const details = [dobStr, profile.birthTime, profile.birthPlace].filter(Boolean).join(", ");

      const intro = `Namaste ${firstName}! Main ${aName} hoon, aapka AI astrologer. ${topics}`;
      const initial = [{ id: uid(), sender: "bot", text: intro, shown: intro }];
      if (details) {
        const t = `Aapki janam details: ${details}`;
        initial.push({ id: uid(), sender: "system", text: t, shown: t });
      }
      setMessages(initial);
      setReady(true);
      trackCustomEvent("ChatStarted", { isTrial: trial });
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [astrologerId]);

  // ---------- clock (uses real time, so a backgrounded phone tab can't cheat or stall it) ----------
  useEffect(() => {
    if (!ready) return;
    let last = Date.now();
    const id = setInterval(() => {
      const now = Date.now();
      const delta = (now - last) / 1000;
      last = now;
      const { isTrial: trial, busy } = live.current;
      if (trial && PAUSE_TRIAL_WHILE_AI_REPLIES && busy) return;
      setRemaining((prev) => (prev <= 0 ? 0 : Math.max(0, prev - delta)));
    }, 250);
    return () => clearInterval(id);
  }, [ready]);

  // time is up
  useEffect(() => {
    if (!ready || secs > 0 || endedRef.current) return;
    endedRef.current = true;
    if (isTrial) {
      trackCustomEvent("FreeMinuteEnded");
      setSheet("trialEnd");
    } else {
      setSheet("extend");
    }
  }, [ready, secs, isTrial]);

  // keep the chat across refreshes
  useEffect(() => {
    if (!ready) return;
    sessionStorage.setItem(
      sessKey,
      JSON.stringify({
        messages: messages
          .filter((m) => m.sender !== "error")
          .map((m) => ({ id: m.id, sender: m.sender, text: m.text })),
        remaining: secs,
        total,
        isTrial,
        navKey: navKeyRef.current,
        savedAt: Date.now(),
      })
    );
  }, [ready, messages, secs, total, isTrial, sessKey]);

  // wallet balance for the payment sheets
  useEffect(() => {
    if ((sheet !== "trialEnd" && sheet !== "extend") || !userId) return;
    axios
      .get(`${API}/users/${userId}/details`)
      .then((res) => setBalance(res.data.wallet?.balance ?? 0))
      .catch((err) => console.error("Failed to fetch balance:", err));
  }, [sheet, userId]);

  // scroll to the newest message (inside the chat only)
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  // ---------- asking the astrologer ----------
  const askAstrologer = async (text) => {
    setLoading(true);
    try {
      // Earlier messages, so the AI can build on the conversation
      const history = messages
        .filter((m) => m.sender === "user" || m.sender === "bot")
        .map((m) => ({ role: m.sender === "user" ? "user" : "assistant", content: m.text }));
      const last = history[history.length - 1];
      if (last && last.role === "user" && last.content === text) history.pop(); // avoid a duplicate on retry

      const res = await axios.post(
        `${API}/chatbot/chat`,
        { query: text, astrologerId, history, timeLeft: secs },
        { headers: { Authorization: `Bearer ${userProfile.token}` } }
      );
      setLoading(false);
      setAiSuggestions(Array.isArray(res.data.suggestions) ? res.data.suggestions : []);
      await typeMessage(res.data.reply);
    } catch (err) {
      console.error(err);
      setLoading(false);
      if (err.response?.status === 402) {
        setRemaining(0); // the server says the chat time is over: show the payment sheet
        return;
      }
      if (err.response?.status === 401) {
        navigate("/login?next=/user/dashboard", { replace: true });
        return;
      }
      setMessages((prev) => [
        ...prev,
        {
          id: uid(),
          sender: "error",
          text:
            err.response?.status === 429
              ? err.response.data?.message || "Thoda dheere pucho."
              : "Jawab nahi aa paaya.",
          retry: text,
        },
      ]);
    }
  };

  const sendMessage = (preset) => {
    const text = (typeof preset === "string" ? preset : input).trim();
    if (!text || !userProfile || loading || replying || ended) return;
    setMessages((prev) => [...prev, { id: uid(), sender: "user", text, shown: text }]);
    setInput("");
    if (!firstSentRef.current) {
      firstSentRef.current = true;
      trackCustomEvent("FirstChatMessage", { isTrial });
    }
    askAstrologer(text);
  };

  sendRef.current = sendMessage;

  const retry = (msg) => {
    if (loading || replying) return;
    setMessages((prev) => prev.filter((m) => m.id !== msg.id));
    askAstrologer(msg.retry);
  };

  // ---------- paying to continue ----------
  const handleContinueAfterTrial = async () => {
    if (!userProfile) return;
    setPayBusy(true);
    setSheetError("");
    try {
      const res = await axios.post(
        `${API}/consultations/ai`,
        { userId, astrologerId, rate },
        { headers: { Authorization: `Bearer ${userProfile.token}` } }
      );
      if (res.data.success) {
        setIsTrial(false);
        setTotal(300);
        setRemaining(300);
        endedRef.current = false;
        setSheet(null);
        addSystem("5 minute add ho gaye. Chat jaari rakho.");
        const pending = sessionStorage.getItem(pendingKey);
        if (pending) {
          sessionStorage.removeItem(pendingKey);
          setTimeout(() => sendRef.current && sendRef.current(pending), 400);
        }
      } else {
        setSheetError(
          res.data.message === "Insufficient balance"
            ? `Wallet mein kam se kam ₹${cost} chahiye.`
            : res.data.message || "Chat jaari nahi ho paayi."
        );
      }
    } catch (err) {
      if (err.response?.data?.message === "Insufficient balance") {
        setSheetError(`Wallet mein kam se kam ₹${cost} chahiye.`);
      } else {
        setSheetError("Chat jaari nahi ho paayi. Dobara try karo.");
      }
    } finally {
      setPayBusy(false);
    }
  };

  const handleExtend = async () => {
    if (!userProfile) return;
    setPayBusy(true);
    setSheetError("");
    try {
      const res = await axios.post(
        `${API}/consultations/deduct`,
        { userId, astrologerId, minutes: EXTEND_MINUTES },
        { headers: { Authorization: `Bearer ${userProfile.token}` } }
      );
      if (res.data.success) {
        setRemaining((prev) => prev + EXTEND_MINUTES * 60);
        endedRef.current = false;
        setSheet(null);
        addSystem(`${EXTEND_MINUTES} minute aur add ho gaye.`);
        const pending = sessionStorage.getItem(pendingKey);
        if (pending) {
          sessionStorage.removeItem(pendingKey);
          setTimeout(() => sendRef.current && sendRef.current(pending), 400);
        }
      } else {
        setSheetError(
          res.data.message === "Insufficient balance"
            ? `Wallet mein kam se kam ₹${cost} chahiye.`
            : res.data.message || "Time add nahi ho paaya."
        );
      }
    } catch (err) {
      setSheetError("Time add nahi ho paaya. Dobara try karo.");
    } finally {
      setPayBusy(false);
    }
  };

  const leaveChat = () => {
    sessionStorage.removeItem(sessKey);
    sessionStorage.removeItem(pendingKey);
    sessionStorage.removeItem("asReturnToChat");
    navigate("/user/dashboard");
  };

  const goWallet = () => sessionStorage.setItem("asReturnToChat", location.pathname);

  // The user picked a follow-up question on the "free minute over" sheet:
  // remember it, then continue (or go to the wallet first). It is sent after payment.
  const askAfterPaying = (q) => {
    sessionStorage.setItem(pendingKey, q);
    if (rate > 0 && notEnough) {
      goWallet();
      navigate("/user/wallet");
      return;
    }
    handleContinueAfterTrial();
  };

  // Same for the paid "add more time" sheet: remember the question, extend, then send it
  const askAfterExtending = (q) => {
    sessionStorage.setItem(pendingKey, q);
    if (rate > 0 && notEnough) {
      goWallet();
      navigate("/user/wallet");
      return;
    }
    handleExtend();
  };

  // ---------- derived UI ----------
  const mm = Math.floor(secs / 60);
  const ss = String(secs % 60).padStart(2, "0");
  const lowTime = !ended && (isTrial ? secs <= 10 : secs <= 60);
  const progress = Math.min(100, (remaining / Math.max(total, remaining, 1)) * 100);
  const hasUserMsg = messages.some((m) => m.sender === "user");
  const lastUserText = [...messages].reverse().find((m) => m.sender === "user")?.text || "";
  const askedSet = new Set(
    messages.filter((m) => m.sender === "user").map((m) => m.text.toLowerCase().trim())
  );
  const followUps = aiSuggestions.length ? aiSuggestions : followUpsFor(lastUserText, astrologerInfo?.categories);
  const nextChips = hasUserMsg
    ? followUps.filter((q) => !askedSet.has(q.toLowerCase())).slice(0, 3)
    : questionsFor(astrologerInfo?.categories);
  const showChips = ready && !ended && !loading && !replying && nextChips.length > 0;
  const chips = nextChips;
  const inputBlocked = ended || loading || replying;

  const boxStyle = box.mobile ? { top: box.top, height: box.height } : undefined;

  return (
    <div
      className="as-chat fixed inset-x-0 top-0 z-[55] bg-[#F5F4FB] md:bottom-0 md:top-[84px]"
      style={boxStyle}
    >
      <style>{chatCss}</style>

      <div className="mx-auto flex h-full w-full max-w-2xl flex-col bg-white md:border-x md:border-[#E3E0F3]">
        {/* ---------- Header ---------- */}
        <header className="as-sky text-white">
          <div className="flex items-center gap-3 px-3 py-2.5">
            <button
              type="button"
              onClick={() => setSheet("confirmEnd")}
              aria-label="Chat se bahar jao"
              className="grid h-9 w-9 shrink-0 place-items-center rounded-full hover:bg-white/10"
            >
              <ArrowLeft size={22} />
            </button>
            <img
              src={astrologerPhoto}
              alt={astrologerInfo?.name || "AI astrologer"}
              className="h-10 w-10 shrink-0 rounded-full object-cover ring-2 ring-white/30"
            />
            <div className="min-w-0 flex-1">
              <p className="as-display truncate text-[17px] font-bold leading-tight">
                {astrologerInfo?.name || "AI astrologer"}
              </p>
              <p className="text-xs text-indigo-200">AI astrologer</p>
            </div>
            <span
              className={`as-display flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1 text-[15px] font-bold ${
                lowTime || ended ? "bg-[#FFB627] text-[#1E1A4D]" : "bg-white/10 text-[#FFB627]"
              }`}
              aria-label="Bacha hua samay"
            >
              <Clock size={15} />
              {isTrial && "Free "}
              {mm}:{ss}
            </span>
          </div>
          <div className="h-1 w-full bg-white/10" aria-hidden="true">
            <div
              className="h-full bg-[#FFB627] transition-[width] duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </header>

        {/* ---------- Messages ---------- */}
        <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-[#F5F4FB] px-3 py-4">
          {!ready && (
            <p className="py-10 text-center text-sm text-slate-500">Chat khul rahi hai...</p>
          )}

          {messages.map((msg) => {
            if (msg.sender === "system") {
              return (
                <p
                  key={msg.id}
                  className="as-msg mx-auto max-w-[90%] rounded-xl bg-[#EEEBFA] px-3 py-2 text-center text-[13px] text-slate-600"
                >
                  {msg.text}
                </p>
              );
            }
            if (msg.sender === "user") {
              return (
                <div key={msg.id} className="as-msg flex justify-end">
                  <div className="max-w-[80%] whitespace-pre-wrap break-words rounded-2xl rounded-br-md bg-[#3A2E9C] px-4 py-2.5 text-[16px] leading-snug text-white">
                    {msg.text}
                  </div>
                </div>
              );
            }
            if (msg.sender === "error") {
              return (
                <div key={msg.id} className="as-msg flex items-end gap-2">
                  <img src={astrologerPhoto} alt="" className="h-8 w-8 shrink-0 rounded-full object-cover" />
                  <div className="max-w-[80%] rounded-2xl rounded-bl-md border border-red-200 bg-red-50 px-4 py-2.5 text-[15px] text-red-700">
                    {msg.text}
                    <button
                      type="button"
                      onClick={() => retry(msg)}
                      className="mt-1.5 flex items-center gap-1.5 text-sm font-semibold text-red-700 underline"
                    >
                      <RotateCcw size={14} /> Dobara bhejo
                    </button>
                  </div>
                </div>
              );
            }
            return (
              <div key={msg.id} className="as-msg flex items-end gap-2">
                <img src={astrologerPhoto} alt="" className="h-8 w-8 shrink-0 rounded-full object-cover" />
                <div className="max-w-[82%] whitespace-pre-wrap break-words rounded-2xl rounded-bl-md border border-[#E3E0F3] bg-white px-4 py-2.5 text-[16px] leading-snug text-slate-800">
                  {renderRich(msg.shown)}
                </div>
              </div>
            );
          })}

          {loading && (
            <div className="as-msg flex items-end gap-2">
              <img src={astrologerPhoto} alt="" className="h-8 w-8 shrink-0 rounded-full object-cover" />
              <div className="flex items-center gap-1.5 rounded-2xl rounded-bl-md border border-[#E3E0F3] bg-white px-4 py-3.5">
                <span className="as-dot h-2 w-2 rounded-full bg-slate-400" />
                <span className="as-dot h-2 w-2 rounded-full bg-slate-400" />
                <span className="as-dot h-2 w-2 rounded-full bg-slate-400" />
              </div>
            </div>
          )}
        </div>

        {/* ---------- Footer: notices, question chips, input ---------- */}
        <div
          className="border-t border-[#E3E0F3] bg-white px-3 pt-2.5"
          style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
        >
          {lowTime && !isTrial && (
            <div className="mb-2 flex items-center justify-between gap-3 rounded-xl bg-[#FFF3D6] px-3.5 py-2.5 text-[14px] text-[#7A4E00]">
              <span>1 minute bacha hai.</span>
              <button
                type="button"
                onClick={() => {
                  setSheetError("");
                  setSheet("extend");
                }}
                className="shrink-0 rounded-full bg-[#FFB627] px-3.5 py-1.5 text-sm font-bold text-[#1E1A4D]"
              >
                {EXTEND_MINUTES} minute aur
              </button>
            </div>
          )}
          {lowTime && isTrial && (
            <p className="mb-2 rounded-xl bg-[#FFF3D6] px-3.5 py-2.5 text-[14px] text-[#7A4E00]">
              Free minute khatam hone wala hai.
            </p>
          )}

          {showChips && (
            <div className="mb-2 flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: "none" }}>
              {chips.map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => sendMessage(q)}
                  className="shrink-0 whitespace-nowrap rounded-full border border-[#DAD6F0] bg-white px-3.5 py-2 text-[14px] font-medium text-[#17143F] transition active:bg-[#EEEBFA]"
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2">
            <input
              className="min-w-0 flex-1 rounded-full border border-[#DAD6F0] bg-white px-4 py-3 text-[16px] text-[#17143F] placeholder:text-slate-400 focus:border-[#3A2E9C] focus:outline-none focus:ring-2 focus:ring-[#3A2E9C]/20 disabled:bg-slate-50"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendMessage()}
              placeholder={ended ? "Chat ka time khatam" : "Apna sawaal likho..."}
              enterKeyHint="send"
              disabled={ended}
            />
            <button
              type="button"
              onClick={() => sendMessage()}
              disabled={inputBlocked || !input.trim()}
              aria-label="Bhejo"
              className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-[#FFB627] text-[#1E1A4D] transition hover:bg-[#FFC24D] active:scale-95 disabled:opacity-40"
            >
              <Send size={20} />
            </button>
          </div>
          <p className="mt-2 text-center text-[11px] text-slate-400">
            Yeh AI ka jawab hai. Zaroori faisle soch-samajhkar lein.
          </p>
        </div>
      </div>

      {/* ---------- Sheets ---------- */}
      {sheet === "confirmEnd" && (
        <Sheet onClose={() => setSheet(null)}>
          <h2 className="as-display text-[22px] font-bold text-[#17143F]">Chat khatam karein?</h2>
          <p className="mt-1 text-[15px] leading-snug text-slate-600">
            Aap dashboard par chale jaoge.
          </p>
          <button type="button" onClick={() => setSheet(null)} className={`${goldBtn} mt-4`}>
            Chat jaari rakho
          </button>
          <button type="button" onClick={leaveChat} className={textBtn}>
            Haan, khatam karo
          </button>
        </Sheet>
      )}

      {sheet === "trialEnd" && (
        <Sheet onClose={() => {}}>
          <h2 className="as-display text-[22px] font-bold text-[#17143F]">Aapka free minute khatam ho gaya</h2>
          {rate > 0 ? (
            <p className="mt-1 text-[15px] leading-snug text-slate-600">
              Chat jaari rakhne ke liye <strong>₹{cost}</strong> ({EXTEND_MINUTES} minute, ₹{rate}/min) wallet se katenge.
              {balance !== null && (
                <>
                  {" "}
                  Aapka balance: <strong>₹{balance}</strong>.
                </>
              )}
            </p>
          ) : (
            <p className="mt-1 text-[15px] text-slate-600">Yeh chat free hai, aap jaari rakh sakte ho.</p>
          )}

          {lastUserText && followUps.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-semibold text-[#17143F]">Aap ye bhi pooch sakte ho:</p>
              <div className="mt-2 flex flex-col gap-2">
                {followUps.map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => askAfterPaying(q)}
                    disabled={payBusy}
                    className="rounded-xl border border-[#DAD6F0] px-3.5 py-2.5 text-left text-[14px] font-medium text-[#17143F] transition active:bg-[#EEEBFA] disabled:opacity-60"
                  >
                    {q}
                  </button>
                ))}
              </div>
              {rate > 0 && (
                <p className="mt-2 text-[12px] text-slate-500">
                Sawaal chunne par ₹{cost} ({EXTEND_MINUTES} minute) wallet se katenge.
                </p>
              )}
            </div>
          )}

          {(sheetError || (rate > 0 && notEnough)) && (
            <p className="mt-3 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
              {sheetError || `Wallet mein kam se kam ₹${cost} chahiye.`}
            </p>
          )}

          {rate > 0 && notEnough ? (
            <Link to="/user/wallet" onClick={goWallet} className={`${goldBtn} mt-4`}>
              Wallet recharge karo
            </Link>
          ) : (
            <button
              type="button"
              onClick={handleContinueAfterTrial}
              disabled={payBusy}
              className={`${goldBtn} mt-4`}
            >
              {payBusy ? "Ek second..." : "Chat jaari rakho"}
            </button>
          )}
          <button type="button" onClick={leaveChat} className={textBtn}>
            Abhi nahi
          </button>
        </Sheet>
      )}

      {sheet === "extend" && (
        <Sheet onClose={() => !ended && setSheet(null)}>
          <h2 className="as-display text-[22px] font-bold text-[#17143F]">
            {ended ? "Aapka chat time khatam ho gaya" : `${EXTEND_MINUTES} minute aur badhao?`}
          </h2>
          {rate > 0 ? (
            <p className="mt-1 text-[15px] leading-snug text-slate-600">
              <strong>₹{cost}</strong> ({EXTEND_MINUTES} minute, ₹{rate}/min) wallet se katenge.
              {balance !== null && (
                <>
                  {" "}
                  Aapka balance: <strong>₹{balance}</strong>.
                </>
              )}
            </p>
          ) : (
            <p className="mt-1 text-[15px] text-slate-600">Yeh chat free hai, time badha sakte ho.</p>
          )}

          {lastUserText && followUps.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-semibold text-[#17143F]">Aap ye bhi pooch sakte ho:</p>
              <div className="mt-2 flex flex-col gap-2">
                {followUps.map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => askAfterExtending(q)}
                    disabled={payBusy}
                    className="rounded-xl border border-[#DAD6F0] px-3.5 py-2.5 text-left text-[14px] font-medium text-[#17143F] transition active:bg-[#EEEBFA] disabled:opacity-60"
                  >
                    {q}
                  </button>
                ))}
              </div>
              {rate > 0 && (
                <p className="mt-2 text-[12px] text-slate-500">
                Sawaal chunne par ₹{cost} ({EXTEND_MINUTES} minute) wallet se katenge.
                </p>
              )}
            </div>
          )}

          {(sheetError || (rate > 0 && notEnough)) && (
            <p className="mt-3 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
              {sheetError || `Wallet mein kam se kam ₹${cost} chahiye.`}
            </p>
          )}

          {rate > 0 && notEnough ? (
            <Link to="/user/wallet" onClick={goWallet} className={`${goldBtn} mt-4`}>
              Wallet recharge karo
            </Link>
          ) : (
            <button type="button" onClick={handleExtend} disabled={payBusy} className={`${goldBtn} mt-4`}>
              {payBusy ? "Ek second..." : `${EXTEND_MINUTES} minute aur`}
            </button>
          )}
          {ended ? (
            <button type="button" onClick={leaveChat} className={textBtn}>
              Dashboard par jao
            </button>
          ) : (
            <button type="button" onClick={() => setSheet(null)} className={textBtn}>
              Rehne do
            </button>
          )}
        </Sheet>
      )}
    </div>
  );
}